import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import type { Message } from '@itsappy/shared';
import { useCustomEmoji } from '@/hooks/queries';
import { cn } from '@/lib/utils';

/**
 * A reaction is either Unicode or `:name:` naming one of the org's own emoji.
 * The custom list is cached by react-query, so this costs a map lookup rather
 * than a request per reaction.
 */
export function ReactionGlyph({ emoji }: { emoji: string }) {
  const { data } = useCustomEmoji();
  const name = /^:([a-z0-9_+-]+):$/.exec(emoji)?.[1];
  const custom = name ? data?.items.find((item) => item.name === name) : undefined;

  if (!custom) return <>{emoji}</>;
  return (
    <img
      src={custom.url}
      alt={emoji}
      title={emoji}
      className="inline-block h-4 w-4 object-contain align-text-bottom"
    />
  );
}

/** Kept clear of the window edges, and of the anchor it sits beside. */
const POPOVER_GAP = 6;
const POPOVER_EDGE = 8;

/**
 * A popover pinned to `anchorRef`, rendered into `document.body`.
 *
 * A reaction picker anchored inside the message list was cut off by that
 * list's own scrollbox — on the first message it lost its top, and on a short
 * bubble near the pane's edge it lost the emoji at the end. Escaping to the
 * body and clamping to the window is what makes it whole wherever the message
 * happens to sit.
 *
 * Dismisses itself on Escape or an outside click, ignoring clicks on the
 * anchor so its own trigger still toggles.
 */
export function AnchoredPopover({
  anchorRef,
  onClose,
  className,
  children,
}: {
  anchorRef: RefObject<HTMLElement>;
  onClose: () => void;
  className?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  /* Laid out after measuring, so the first paint is not in the wrong corner. */
  const [style, setStyle] = useState<CSSProperties>({
    position: 'fixed',
    top: 0,
    left: 0,
    visibility: 'hidden',
  });

  const place = useCallback(() => {
    const anchor = anchorRef.current;
    const element = ref.current;
    if (!anchor || !element) return;
    const from = anchor.getBoundingClientRect();
    const box = element.getBoundingClientRect();
    const above = from.top - box.height - POPOVER_GAP;
    setStyle({
      position: 'fixed',
      /* Above the anchor, which is where the messages are; below it only when
         there is no room, as on the first message in a short conversation. */
      top:
        above >= POPOVER_EDGE
          ? above
          : Math.min(from.bottom + POPOVER_GAP, window.innerHeight - box.height - POPOVER_EDGE),
      left: Math.min(
        Math.max(from.left + from.width / 2 - box.width / 2, POPOVER_EDGE),
        window.innerWidth - box.width - POPOVER_EDGE,
      ),
    });
  }, [anchorRef]);

  useLayoutEffect(place, [place]);

  /* Opening a chat scrolls the list to the newest message, which moved the
     anchor out from under a popover measured once at mount — it opened over
     empty space below the window. Capture, so a scroll inside the message
     list counts and not only one on the window.
     The observer covers the popover's own growth: six quick reactions become
     a picker fifteen times the height, which off the old measurement hung
     past the bottom of the window. */
  useEffect(() => {
    const observer = new ResizeObserver(place);
    if (ref.current) observer.observe(ref.current);
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [place]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (ref.current?.contains(target) || anchorRef.current?.contains(target)) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    // Deferred: the click that opened this would otherwise close it.
    const timer = window.setTimeout(
      () => window.addEventListener('mousedown', onPointerDown),
      0,
    );
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onPointerDown);
    };
  }, [anchorRef, onClose]);

  return createPortal(
    <div ref={ref} style={style} className={cn('z-[70]', className)}>
      {children}
    </div>,
    document.body,
  );
}

/**
 * The chip row under a message — one chip per emoji, outlined while the viewer
 * is among the reactors, and clicking it takes their reaction back.
 *
 * Shared so the channel, a thread reply and the floating chat cannot drift
 * into three slightly different ideas of what a reaction looks like.
 */
export function ReactionChips({
  message,
  viewerId,
  onReact,
  className,
}: {
  message: Message;
  viewerId?: string;
  onReact: (messageId: string, emoji: string) => void;
  className?: string;
}) {
  if (message.reactions.length === 0) return null;
  return (
    <div className={cn('mt-1.5 flex flex-wrap gap-1', className)}>
      {message.reactions.map((reaction) => (
        <button
          key={reaction.emoji}
          type="button"
          onClick={() => onReact(message.id, reaction.emoji)}
          aria-pressed={Boolean(viewerId && reaction.userIds.includes(viewerId))}
          className={cn(
            'chip border bg-surface-2 hover:border-brand-500',
            viewerId && reaction.userIds.includes(viewerId)
              ? 'border-brand-500'
              : 'border-surface-4',
          )}
        >
          <ReactionGlyph emoji={reaction.emoji} /> {reaction.userIds.length}
        </button>
      ))}
    </div>
  );
}
